import { Feature } from '@/pinia/feature'

export const co2: Feature = {
  name: 'co2',
  enabled: true,
  title: {
    en: 'CO2',
    sv: 'Koldioxid'
  },
  info: {
    en: 'Carbon dioxide levels are collected as averages over 10-minute intervals and aggregated over longer periods of time, then represented in parts per million (ppm).',
    sv: 'Koldioxidhalterna hämtas som medelvärden över 10-minutersintervall och aggregeras över längre tidsperioder för att sedan representeras i parts per million (ppm).'
  },
  component: 'DashboardDataSectionFlat',
  isRoute: true,
  props: {
    dataSources: [
      {
        id: 'climate.co2.kpi.0',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.co2.kpi',
        path: 'kpis'
      },
      {
        id: 'climate.co2.chart',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.co2.timeseries',
        path: 'timeSeries[0]'
      },
      {
        id: 'climate.co2.histogram',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.co2.histogram',
        path: 'histograms[0].range'
      },
      {
        id: 'climate.co2.toplist.average',
        type: 'fetch',
        fetch: 'fetch.indoorclimate.co2.toplist.average',
        path: ''
      },
      {
        id: 'climate.co2.colorscale',
        type: 'value',
        value: {
          type: 'INTERPOLATE',
          cutoffs: [
            {
              value: 100,
              color: '#73D13D'
            },
            {
              value: 750,
              color: '#73D13D'
            },
            {
              value: 775,
              color: '#FFA940'
            },
            {
              value: 800,
              color: '#FF4D4F'
            },
            {
              value: 1000,
              color: '#FF4D4F'
            }
          ]
        }
      }
    ]
  },
  children: [
    {
      name: 'co2KPICard',
      enabled: true,
      title: {
        en: 'Mean value',
        sv: 'Medelvärde'
      },
      info: {
        en: 'Average over the selected time interval and chosen rooms.',
        sv: 'Medelvärde över valt tidsintervall och valda rum.'
      },
      component: 'KPICard',
      props: {
        colorScale: 'climate.co2.colorscale',
        dataSource: 'climate.co2.kpi.0',
        isLoading: false,
        showOnMap: false,
        showOnMapEnabled: true,
        first: true,
        kpiFormat: {
          format: {
            type: 'intl-number',
            maximumFractionDigits: 1
          },
          steps: [
            {
              format: {
                type: 'format-string',
                format: '%s ppm'
              }
            }
          ]
        },
        metadataFormat: {
          space: {}
        },
        metadataLabels: {
          space: {
            en: 'Number of spaces',
            sv: 'Antal rum'
          }
        }
      }
    },
    {
      name: 'divider',
      title: {
        en: 'Divider',
        sv: 'Avdelare'
      },
      component: 'DividerComponent',
      enabled: true,
      props: {
        width: 'auto',
        fullscreenWidth: '70%'
      }
    },
    {
      name: 'co2',
      enabled: true,
      title: {
        en: 'Change over time',
        sv: 'Förändring över tid'
      },
      info: {
        en: 'The graph visualizes the average indoor carbon dioxide level over a given time period. The curve represents the varying levels of carbon dioxide and provides insight into how the carbon dioxide level changes over time. This information is valuable for understanding and optimizing indoor air quality as well as creating a healthy and sustainable indoor environment.  \n\nThe gray areas in the graph indicate the absence of available data for certain periods or data that has been deliberately filtered out by the user (for example, weekends).',
        sv: 'Grafen visualiserar koldioxidhaltens medelvärde inomhus över en given tidsperiod. Kurvan representerar de varierande nivåerna av koldioxid och ger insikt i hur koldioxidhalten förändras över tid. Denna information är värdefull för att förstå och optimera inomhusluftkvaliteten samt skapa en hälsosam och hållbar inomhusmiljö.  \n\nDe gråa områdena i grafen markerar avsaknaden av tillgänglig data under vissa tidsperioder eller data som medvetet har filtrerats bort av användaren (till exempel helger).'
      },
      component: 'CardChart',
      props: {
        enableFullscreen: true,
        chart: {
          type: 'timeseries',
          dataFormat: {
            format: {
              type: 'intl-number',
              maximumFractionDigits: 1
            },
            steps: [
              {
                format: {
                  type: 'format-string',
                  format: '%s ppm'
                }
              }
            ]
          },
          colorScale: 'climate.co2.colorscale',
          labels: [
            {
              sv: 'Koldioxid',
              en: 'CO2'
            }
          ],
          lineFormats: [
            {
              style: 'solid'
            }
          ],
          dataSources: ['climate.co2.chart'],
          missingDataMessage: {
            en: 'Missing data',
            sv: 'Saknar data'
          }
        }
      }
    },
    {
      name: 'co2-hist',
      enabled: true,
      title: {
        en: 'Dispersion',
        sv: 'Spridning'
      },
      info: {
        en: 'This bar chart provides an overview of the indoor carbon dioxide level distribution over a given time period.  \n\nEach bar represents different carbon dioxide intervals, and its height indicates the frequency of observations within each range. By analyzing the bar chart, one gets a clear picture of the distribution of carbon dioxide levels, which can be crucial for optimizing the indoor environment and ensuring good air quality.',
        sv: 'Detta stapeldiagram ger en översikt över spridningen av koldioxidhalten inomhus under en given tidsperiod.  \n\nVarje stapel representerar olika koldioxidintervall och dess höjd indikerar frekvensen av observationer inom varje intervall. Genom att analysera stapeldiagrammet får man en tydlig bild av fördelningen av koldioxidhalten, vilket kan vara avgörande för att optimera inomhusmiljön och säkerställa god luftkvalitet.'
      },
      component: 'CardChart',
      props: {
        enableFullscreen: true,
        chart: {
          type: 'histogram',
          codomainFormat: {
            format: {
              type: 'intl-number',
              style: 'percent',
              maximumFractionDigits: 1
            }
          },
          domainFormat: {
            format: {
              type: 'intl-number',
              maximumFractionDigits: 1
            },
            steps: [
              {
                format: {
                  type: 'format-string',
                  format: '%s ppm'
                }
              }
            ]
          },
          valueFilterTreshold: 0.001,
          codomainTicks: {
            values: [0, 0.5, 1]
          },
          label: {
            sv: 'Koldioxid',
            en: 'Carbon dioxide'
          },
          missingDataMessage: {
            en: 'Missing data',
            sv: 'Saknar data'
          },
          colorScale: 'climate.co2.colorscale',
          dataSource: 'climate.co2.histogram'
        }
      }
    },
    {
      name: 'index-toplist',
      enabled: true,
      title: {
        en: 'Toplist',
        sv: 'Topplista'
      },
      component: 'CardTopList',
      props: {
        dataSource: 'climate.co2.toplist.average',
        info: 'Some info about the toplist',
        format: {
          format: {
            type: 'intl-number',
            maximumFractionDigits: 1
          },
          steps: [
            {
              format: {
                type: 'format-string',
                format: '%s ppm'
              }
            }
          ]
        },
        barOptions: [
          {
            label: {
              en: 'Carbon dioxide',
              sv: 'Koldioxid'
            },
            color: '#0BA5EC',
            gradient: true,
            tooltip: true
          },
          {
            label: {
              en: 'Not measured',
              sv: 'Ej mätt'
            },
            color: '#E0E0E0',
            gradient: true,
            tooltip: false
          }
        ],
        errorMessages: {
          NO_DATA: {
            en: 'No data available',
            sv: 'Ingen data tillgänglig'
          },
          INVALID_FILTER: {
            en: 'Filter on complex to show data',
            sv: 'Filtrera på komplex för att visa data'
          }
        }
      }
    }
  ]
}
